/*
  GEMDRAW — Diamond card

  Turns the approved render into an object that can be handled. The card leans
  toward a pointer, a studio reflection follows the cursor across its surface,
  the lit edge comes up, one metallic pass crosses it on arrival — and it can
  be dragged, which is the part that had to be built rather than styled.

  THE DRAG IS REAL BECAUSE THE COPY SAYS IT IS. "Drag to explore" printed under
  an object that does not move is the worst kind of interface writing, and the
  fix is not to delete the line. So: pointer capture, a bounded turn, an idle
  that pauses while a hand is on it, and a spring home on release.

  IT IS BOUNDED, AND THE BOUND IS A PROPERTY OF THE ARTWORK. The render is a
  photograph with its perspective baked into the pixels. Past roughly fourteen
  degrees the CSS rotation starts arguing with the camera the image was shot
  with and the card reads as bent rather than as turned. The ceiling is not a
  preference; it is where the illusion breaks.

  IT IS NOT A CONTROL. No focus, no role, no keyboard binding. Nothing is
  behind the card — it has one face, and turning it reveals nothing that was
  not already visible — so the gesture is presentation. A tab stop that only
  tips is a stop that leads nowhere, and a keyboard user is owed the content,
  which they already have.

  VERTICAL SCROLL ALWAYS WINS. On a touch screen the drag only takes over once
  the gesture is clearly horizontal. Until then every move belongs to the page.
  A decorative interaction that can trap a scroll on a phone is a bug however
  well it animates.

  EVERYTHING IT DOES IS A CUSTOM PROPERTY. The script's entire output is five
  values and three attributes. All the drawing lives in gemdraw-diamond.css, so
  the look can be retuned without opening this file and this file cannot leak
  layout.

  NO LIBRARIES. Pointer events, one IntersectionObserver, one rAF and CSS
  custom properties.
*/

(function () {
  'use strict';

  const SELECTOR = '[data-gd-dcard-grip]';
  const TILT = '[data-gd-dcard-tilt]';
  const GLEAM = '.gd-dcard__gleam';
  const SECTION = '[data-gd-dcard-section]';
  const HINT = '[data-gd-dcard-hint]';

  const ACTIVE = 'data-gd-dcard-active';
  const DRAG = 'data-gd-dcard-drag';
  const PASS = 'data-gd-dcard-pass';
  const IDLE = 'data-gd-dcard-idle';
  const SEEN = 'data-gd-dcard-seen';

  /*
    THE ENVELOPE.

    Four degrees of pitch and six of yaw for a hover, fourteen of yaw for a
    drag. The hover is smaller because it is involuntary — the viewer did not
    ask for it — and the drag is larger because they did. Past fourteen the
    render's baked-in perspective starts to argue with the CSS rotation.
  */
  const MAX_TILT_X = 4;
  const MAX_TILT_Y = 6;
  const MAX_DRAG_Y = 14;
  const MAX_DRAG_X = 5;
  const MAX_SHIFT = 16;

  /* How much of a gesture becomes rotation. Tuned so a comfortable drag across
     the card's own width reaches the limit and no further — a card that hits
     its stop in the first inch feels broken rather than bounded. */
  const YAW_PER_PX = 0.05;
  const PITCH_PER_PX = 0.035;
  const SHIFT_PER_PX = 0.06;

  /* On touch, the point at which a gesture stops being a scroll. */
  const TOUCH_SLOP = 8;

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clamp(value, limit) {
    return Math.max(-limit, Math.min(limit, value));
  }

  function Card(grip, tilt) {
    this.grip = grip;
    this.tilt = tilt;
    this.gleam = grip.querySelector(GLEAM);

    this.box = null;
    this.x = 0;
    this.y = 0;
    this.frame = 0;

    /* Drag state. `pointer` is the id being tracked; `active` is whether the
       gesture has committed, which on touch happens later than pointerdown. */
    this.pointer = null;
    this.active = false;
    this.startX = 0;
    this.startY = 0;
    this.dragX = 0;
    this.dragY = 0;
    this.dragFrame = 0;

    /* One pass per visit. It is armed on leave rather than cleared on enter, so
       a cursor crossing the card twice gets two passes and a cursor sitting on
       it gets one. */
    this.armed = true;
  }

  /* Measured on enter and on resize, never during a move. */
  Card.prototype.measure = function () {
    this.box = this.grip.getBoundingClientRect();
  };

  Card.prototype.write = function () {
    this.frame = 0;
    if (!this.box || !this.box.width || !this.box.height) return;

    /* Position within the grip, as -0.5 to 0.5 from its centre. */
    const px = (this.x - this.box.left) / this.box.width;
    const py = (this.y - this.box.top) / this.box.height;
    const cx = px - 0.5;
    const cy = py - 0.5;

    /*
      Y drives the X axis and vice versa. Moving the cursor up tips the top of
      the card away from the viewer, which is what leaning toward a pointer
      actually looks like — the opposite pairing reads as the card flinching.
    */
    const style = this.tilt.style;
    style.setProperty('--gd-dcard-rx', (-cy * MAX_TILT_X * 2).toFixed(2) + 'deg');
    style.setProperty('--gd-dcard-ry', (cx * MAX_TILT_Y * 2).toFixed(2) + 'deg');

    /* The reflection sits under the cursor. Clamped, because the grip is wider
       than the card and the light should stay on the object. */
    style.setProperty('--gd-card-pointer-x', (Math.min(1, Math.max(0, px)) * 100).toFixed(1) + '%');
    style.setProperty('--gd-card-pointer-y', (Math.min(1, Math.max(0, py)) * 100).toFixed(1) + '%');
  };

  /* Pointer events outrun the display, so the handler records and the frame
     writes. Without this the same property is set several times between two
     paints, which is work thrown away. */
  Card.prototype.schedule = function () {
    if (this.frame) return;
    const card = this;
    this.frame = window.requestAnimationFrame(function () {
      card.write();
    });
  };

  Card.prototype.rest = function () {
    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    /* Cleared rather than set to zero, so the card falls back to the
       stylesheet's own resting values — the CSS stays the single source of
       truth for what "at rest" means. */
    const style = this.tilt.style;
    style.removeProperty('--gd-dcard-rx');
    style.removeProperty('--gd-dcard-ry');
    style.removeProperty('--gd-card-pointer-x');
    style.removeProperty('--gd-card-pointer-y');
  };

  /* Drag ------------------------------------------------------------------- */

  Card.prototype.writeDrag = function () {
    this.dragFrame = 0;

    const style = this.tilt.style;
    style.setProperty('--gd-dcard-ry', clamp(this.dragX * YAW_PER_PX, MAX_DRAG_Y).toFixed(2) + 'deg');
    style.setProperty('--gd-dcard-rx', clamp(-this.dragY * PITCH_PER_PX, MAX_DRAG_X).toFixed(2) + 'deg');
    style.setProperty('--gd-dcard-tx', clamp(this.dragX * SHIFT_PER_PX, MAX_SHIFT).toFixed(1) + 'px');
  };

  Card.prototype.scheduleDrag = function () {
    if (this.dragFrame) return;
    const card = this;
    this.dragFrame = window.requestAnimationFrame(function () {
      card.writeDrag();
    });
  };

  /*
    The gesture commits. From here the pointer belongs to the card: capture
    means a drag that leaves the element still tracks, and still ends, which is
    what stops a card being left turned when a cursor is released somewhere
    else on the page.
  */
  Card.prototype.beginDrag = function (event) {
    this.active = true;
    this.grip.setAttribute(DRAG, '');

    if (typeof this.grip.setPointerCapture === 'function') {
      try {
        this.grip.setPointerCapture(event.pointerId);
      } catch (error) {
        /* Capture is a nicety, not a requirement: without it the pointerup
           listener on the element still ends the gesture in every ordinary
           case. Nothing here should throw its way out of an input handler. */
      }
    }
  };

  Card.prototype.endDrag = function (event) {
    if (this.dragFrame) {
      window.cancelAnimationFrame(this.dragFrame);
      this.dragFrame = 0;
    }

    if (this.pointer !== null && typeof this.grip.releasePointerCapture === 'function') {
      try {
        this.grip.releasePointerCapture(this.pointer);
      } catch (error) {
        /* Already released, or never captured. */
      }
    }

    this.pointer = null;
    this.active = false;
    this.grip.removeAttribute(DRAG);

    /* The transition comes back with the attribute, and clearing the angles is
       what makes the card spring home rather than snap. The shift is cleared
       with them so the object returns to the pose the composition was built
       around. */
    const style = this.tilt.style;
    style.removeProperty('--gd-dcard-tx');

    /* A mouse that is still over the card goes back to following the pointer;
       a finger that has lifted has nothing to follow. */
    if (event && event.pointerType === 'mouse' && finePointer.matches && !reduceMotion.matches) {
      this.schedule();
    } else {
      this.rest();
    }
  };

  /*
    ONE METALLIC PASS.

    The attribute is removed on animationend rather than tracked in a flag, so
    the animation is genuinely one-shot: it cannot be re-armed while it is
    running and it leaves nothing behind when it finishes.
  */
  Card.prototype.pass = function () {
    if (!this.gleam || !this.armed || reduceMotion.matches) return;
    this.armed = false;

    const gleam = this.gleam;
    gleam.removeAttribute(PASS);

    /* Reading offsetWidth forces the removal to take effect before the
       attribute goes back on. Without it the browser coalesces both changes and
       the animation never restarts. */
    void gleam.offsetWidth;
    gleam.setAttribute(PASS, '');
  };

  function bind(grip) {
    if (grip.dataset.gdDcardBound === 'true') return;

    const tilt = grip.querySelector(TILT);
    if (!tilt) return;

    grip.dataset.gdDcardBound = 'true';

    const card = new Card(grip, tilt);

    const gleam = card.gleam;
    if (gleam) {
      gleam.addEventListener('animationend', function () {
        gleam.removeAttribute(PASS);
      });
    }

    grip.addEventListener('pointerenter', function (event) {
      if (event.pointerType !== 'mouse' || !finePointer.matches) return;

      card.measure();
      grip.setAttribute(ACTIVE, '');
      card.pass();
    });

    grip.addEventListener(
      'pointermove',
      function (event) {
        /* A committed drag owns every move, whatever the input was. */
        if (card.pointer === event.pointerId) {
          const dx = event.clientX - card.startX;
          const dy = event.clientY - card.startY;

          /*
            On touch the gesture has not committed yet. It becomes a drag only
            once it has travelled far enough AND is clearly more horizontal
            than vertical; anything else is the page being scrolled and is left
            alone. touch-action: pan-y in the stylesheet is the other half of
            this — together they mean a vertical swipe over the card scrolls
            normally and never stutters.
          */
          if (!card.active) {
            if (Math.abs(dx) < TOUCH_SLOP || Math.abs(dx) <= Math.abs(dy)) return;
            card.beginDrag(event);
          }

          card.dragX = dx;
          card.dragY = dy;
          card.scheduleDrag();
          return;
        }

        if (event.pointerType !== 'mouse' || !finePointer.matches) return;
        if (reduceMotion.matches) return;

        card.x = event.clientX;
        card.y = event.clientY;
        card.schedule();
      },
      { passive: true }
    );

    grip.addEventListener('pointerleave', function () {
      /* A drag in progress survives the pointer leaving the box — that is what
         the capture is for. Only the hover state is dropped here. */
      grip.removeAttribute(ACTIVE);
      if (card.pointer !== null) return;

      card.rest();
      card.armed = true;
    });

    /*
      The gesture starts. A mouse commits immediately, because a mouse has no
      other use for a press on a decorative object. A finger does not: its
      press might be the beginning of a scroll, and that has to win.
    */
    grip.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      card.pointer = event.pointerId;
      card.startX = event.clientX;
      card.startY = event.clientY;
      card.dragX = 0;
      card.dragY = 0;

      if (event.pointerType === 'mouse') {
        card.beginDrag(event);

        /* Only for a mouse, and only once the drag has committed: without it
           a drag across the card selects the copy beside it. On touch this
           would also block the scroll, which is why it is not there. */
        event.preventDefault();
      }
    });

    grip.addEventListener('pointerup', function (event) {
      if (card.pointer !== event.pointerId) return;
      card.endDrag(event);
    });

    grip.addEventListener('pointercancel', function (event) {
      if (card.pointer !== event.pointerId) return;
      card.endDrag(event);
    });

    /* The browser's own image drag would otherwise pick the render up and
       carry it out of the page mid-gesture. */
    grip.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    return card;
  }

  /*
    THE HINT NAMES THE GESTURE THE DEVICE ACTUALLY HAS.

    "Drag" on a phone is wrong and "swipe" on a desktop is wrong, and the
    difference is not cosmetic — it is the difference between an instruction
    someone can follow and one they cannot. Both strings come from the theme
    editor; this only chooses between them.
  */
  function labelHint(scope) {
    (scope || document).querySelectorAll(HINT).forEach(function (hint) {
      const text = hint.querySelector('.gd-dcard__hint-text');
      if (!text) return;

      const drag = hint.getAttribute('data-gd-drag-label');
      const swipe = hint.getAttribute('data-gd-swipe-label');
      const wanted = finePointer.matches ? drag : swipe;

      if (wanted && text.textContent !== wanted) text.textContent = wanted;
    });
  }

  /*
    OFF SCREEN, NOTHING RUNS.

    Six shafts, seven stones, three catches, a bloom and two arrows is the
    heaviest idle in the build. One observer per section writes a single
    attribute and the stylesheet pauses all of it.

    THE DEFAULT IS RUNNING, NOT PAUSED. The attribute is only ever written to
    stop things, so a failed script leaves the scene animating as authored
    rather than frozen. The second attribute is the opposite — it is written
    once, when the section is first seen, to fade the field up — and the
    stylesheet only hides the field behind it while .gd-motion is present,
    which is to say only when this script's sibling has already proven that
    scripting works.
  */
  let idleObserver = null;

  function getIdleObserver() {
    if (idleObserver || !('IntersectionObserver' in window)) return idleObserver;

    idleObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.removeAttribute(IDLE);
            entry.target.setAttribute(SEEN, '');
          } else {
            entry.target.setAttribute(IDLE, 'rest');
          }
        });
      },
      { rootMargin: '100% 0px' }
    );

    return idleObserver;
  }

  const sections = [];

  function watchSection(el) {
    if (el.dataset.gdDcardWatched === 'true') return;

    const io = getIdleObserver();
    if (!io) {
      /* No observer: show the field and leave everything running. */
      el.setAttribute(SEEN, '');
      return;
    }

    el.dataset.gdDcardWatched = 'true';
    sections.push(el);
    io.observe(el);
  }

  const cards = [];

  function init(root) {
    const scope = root || document;

    scope.querySelectorAll(SELECTOR).forEach(function (grip) {
      const card = bind(grip);
      if (card) cards.push(card);
    });

    scope.querySelectorAll(SECTION).forEach(watchSection);
    if (scope !== document && scope.matches && scope.matches(SECTION)) watchSection(scope);

    labelHint(scope);
  }

  /*
    The measured box goes stale when the page reflows. Re-measured on the next
    frame after a resize rather than on every event, and only for cards a
    pointer is currently on — anything else measures again on its next enter.
  */
  let resizeFrame = 0;
  window.addEventListener(
    'resize',
    function () {
      if (resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(function () {
        resizeFrame = 0;
        cards.forEach(function (card) {
          if (card.grip.hasAttribute(ACTIVE)) card.measure();
        });
      });
    },
    { passive: true }
  );

  /* The preference can change while the page is open. Every card resolves to
     its still state immediately — but the drag stays available, because it is
     a direct manipulation rather than an animation and removing it would take
     away function rather than motion. */
  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', function () {
      if (!reduceMotion.matches) return;
      cards.forEach(function (card) {
        card.grip.removeAttribute(ACTIVE);
        card.rest();
      });
    });
  }

  /* A pointing device can be added or removed mid-session, and the word under
     the card has to follow it. */
  if (typeof finePointer.addEventListener === 'function') {
    finePointer.addEventListener('change', function () {
      labelHint(document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* The theme editor replaces a section's markup wholesale, so anything bound
     to the old nodes is gone with them. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /*
    EVERYTHING HELD ON THE OLD NODES IS RELEASED.

    Per-element listeners die with the elements they were attached to, but the
    two module-level registries do not: without this, every edit of this section
    in the theme editor would leave another detached card in the resize loop and
    another detached section under the observer.
  */
  document.addEventListener('shopify:section:unload', function () {
    for (let i = cards.length - 1; i >= 0; i -= 1) {
      if (!document.contains(cards[i].grip)) cards.splice(i, 1);
    }

    for (let i = sections.length - 1; i >= 0; i -= 1) {
      if (document.contains(sections[i])) continue;
      if (idleObserver) idleObserver.unobserve(sections[i]);
      sections.splice(i, 1);
    }
  });
})();
